import { Link } from "react-router-dom";
import type { NewsItem } from "@/mocks/types";
import { formatDate } from "@/utils/format";

interface NewsCardProps {
  news: NewsItem;
  index?: number;
  layout?: "row" | "column";
}

export default function NewsCard({ news, index = 0, layout = "row" }: NewsCardProps) {
  if (layout === "column") {
    return (
      <Link
        to={`/news/detail/${news.id}`}
        className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200 hover:border-primary-300 transition-colors duration-300 cursor-pointer animate-fade-up"
        style={{ animationDelay: `${Math.min(index, 8) * 55}ms` }}
      >
        <div className="relative w-full h-44 md:h-48 overflow-hidden bg-background-200">
          <img
            src={news.cover}
            alt={news.title}
            title={`${news.title} 动漫资讯`}
            className="w-full h-full object-cover transition-transform duration-500 ease-smooth group-hover:scale-[1.05]"
          />
          <span className="absolute top-3 left-3 px-2 py-0.5 rounded-md bg-primary-500 text-[11px] text-background-50 font-label">
            {news.category}
          </span>
        </div>
        <div className="p-4">
          <h3 className="font-heading font-semibold text-foreground-950 text-base leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
            {news.title}
          </h3>
          <p className="mt-2 text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-2">
            {news.summary}
          </p>
          <div className="mt-3 flex items-center gap-3 text-[11px] text-foreground-500 font-label">
            <span>{formatDate(news.publishedAt)}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" />
              {news.readCount}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/news/detail/${news.id}`}
      className="group flex gap-3 md:gap-5 p-3 md:p-4 bg-background-50 rounded-lg border border-background-200 hover:border-primary-300 transition-colors duration-300 cursor-pointer animate-fade-up"
      style={{ animationDelay: `${Math.min(index, 8) * 55}ms` }}
    >
      <div className="relative w-28 h-20 sm:w-40 sm:h-28 md:w-56 md:h-[148px] shrink-0 rounded-md overflow-hidden bg-background-200">
        <img
          src={news.cover}
          alt={news.title}
          title={`${news.title} 动漫资讯`}
          className="w-full h-full object-cover transition-transform duration-500 ease-smooth group-hover:scale-[1.05]"
        />
      </div>
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="px-2 py-0.5 rounded-md bg-primary-100 text-[11px] text-primary-700 font-label">
            {news.category}
          </span>
          <span className="text-[11px] text-foreground-500 font-label">{formatDate(news.publishedAt)}</span>
        </div>
        <h3 className="mt-2 font-heading font-semibold text-foreground-950 text-sm sm:text-base md:text-lg leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
          {news.title}
        </h3>
        <p className="mt-1.5 text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-2 hidden sm:block">
          {news.summary}
        </p>
        <div className="mt-auto pt-2 flex items-center gap-3 text-[11px] text-foreground-500 font-label">
          <span className="flex items-center gap-1">
            <i className="ri-user-3-line" />
            {news.author}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line" />
            {news.readCount}
          </span>
        </div>
      </div>
    </Link>
  );
}